// Pure unit-conversion helpers for the Pickups UI.
//
// Tile is counted in BOXES on the floor, so pickups are shown/entered in boxes by
// default (falling back to sq ft when an item has no box size). Stock, pickups, and
// audit are ALWAYS stored in sq ft — box is a display/entry convenience only, so every
// entered quantity is converted back to sq ft before it reaches the backend.

export type PickupUnit = 'box' | 'sqft';

// round to 2dp (matches shared/src/sqft.ts so box products reconcile to the penny)
export const round2 = (x: number) => Math.round(x * 100) / 100;

// Convert a sq ft area into the chosen display unit (boxes when in box mode).
export function toUnit(areaSqft: number, unit: PickupUnit, sqftPerBox: number): number {
  if (unit === 'box' && sqftPerBox > 0) return areaSqft / sqftPerBox;
  return areaSqft;
}

/**
 * The `<input>` `max` for a pickup line, in the chosen unit and rounded to 2dp.
 *
 * The field's value and its "remaining" label are 2dp (via the modal's fmtQty), but
 * box↔sqft conversion can leave the raw `toUnit` result a hair below an integer —
 * e.g. a 13-box line whose sq ft was stored as round2(13 * spb) converts back to
 * 12.9999999998. Using that raw float as the native `max` made the browser reject a
 * fully-valid pick-all ("Please select a value that is no more than 13") because the
 * seeded value (13) exceeded it. Rounding the max keeps it in lockstep with the
 * displayed value; the submit-time guard (`resolvePickupQtySqft`) already tolerates
 * the same drift.
 */
export function pickupMaxInUnit(
  remainingSqft: number,
  unit: PickupUnit,
  sqftPerBox: number,
): number {
  return round2(toUnit(remainingSqft, unit, sqftPerBox));
}

/**
 * Resolve a per-line pickup quantity (entered in the chosen unit) back to sq ft for
 * the backend payload.
 *
 * - sqft mode (or no box size configured) → the typed value passes through unchanged.
 * - box mode → `boxes * sqftPerBox`, rounded to 2dp. To avoid rounding drift when the
 *   user collects everything, if the entered box qty is ≥ the full remaining in boxes
 *   (within 1e-6), the EXACT `remainingSqft` is returned instead of the rounded product.
 * - Over-entry (chosen-unit qty > remaining-in-chosen-unit) returns `{ error: 'over' }`.
 */
export function resolvePickupQtySqft(
  rawInput: number,
  unit: PickupUnit,
  remainingSqft: number,
  sqftPerBox: number,
): { sqft: number } | { error: 'over' } {
  if (unit === 'box' && sqftPerBox > 0) {
    const remainingBoxes = remainingSqft / sqftPerBox;
    if (rawInput > remainingBoxes + 1e-6) return { error: 'over' };
    // Pick-all exactness: avoid float drift from boxes * spb when collecting the rest.
    if (rawInput >= remainingBoxes - 1e-6) return { sqft: remainingSqft };
    return { sqft: round2(rawInput * sqftPerBox) };
  }
  // sqft mode (or no box size configured): the typed value is already the area.
  if (rawInput > remainingSqft + 1e-9) return { error: 'over' };
  return { sqft: rawInput };
}
